import React, { useCallback, useEffect, useState } from 'react';
import Dialog from '../../../../../dialog/Dialog';
import Editor from '../../../../../FormControl/Editor';
import Loading from '../../../../../Loading/Loading';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Alert, Chip, Divider, Typography } from '@mui/material';
import ButtonStartIcon from '../../../../../button/ButtonStartIcon';
import { useSelector } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import DialogAttachMedia from '../../../../../dialog/DialogAttachMedia';
import ChipMedia from '../../../../../chip/ChipMedia';
import { getGrowthGoalsByStudentAndDR } from '../../../../../../services/growth-goals/growth-goals.service';
import { useParams } from 'react-router-dom';
import { handlerErrorV2 } from '../../../../../../utils/errors';
import useGrowthGoals from '../../../../../../hooks/growth-goals/useGrowthGoals';
import useUpload from '../../../../../../hooks/upload/useUpload';

const DialogGrowthGoals = ({ onClose, open, formData }) => {
  const { curriculumId, ulId } = useParams();
  const [form, setForm] = useState(formData);
  const [errors, setError] = useState(null);
  const [mode, setMode] = useState('');
  const [modal, setModal] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const permissions = useSelector((state) => state?.session?.permissions?.['marking-class'] || {});
  const student = useSelector((state) => state?.classMarking?.student);

  const { deleteFiles, uploadFile } = useUpload();
  const { createGrowthGoalsByStudentAndDR } = useGrowthGoals();

  const onLoad = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getGrowthGoalsByStudentAndDR(curriculumId, ulId, student._id, formData.questionId);
      if (!response.data && response.error) {
        setForm({ ...formData, comment: '' });
      } else {
        setForm(response.data);
      }
    } catch (error) {
      if (message === 'Growth goals not found') {
        setForm({ ...formData, comment: '' });
      } else {
      const message = handlerErrorV2(error);
      setError({ error: true, message });
      }
    } finally {
      setIsLoading(false);
    }
  }, [curriculumId, ulId, student, formData]);

  const onMode = () => {
    if (isEmpty(form.comment)) {
      setMode('');
    } else {
      setMode('editing');
    }
  };

  const onConfirm = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const media = form.media
        ?.filter((item) => item.type === 'file' && item.file)
        ?.map((item) => ({
          ...item,
          id: item._id || item.id,
          type: item.mimetype
        }));

      if (media?.length > 0) {
        const resultUpload = await uploadFile(media, 'uploads');
        if (resultUpload.error) {
          setError(resultUpload);
          return;
        }
        const newMedia = resultUpload.data;
        form.media = form.media
          .map((item) => {
            if (item.type === 'file') {
              const find = newMedia.find((media) => media.id === item.id);
              if (find) {
                return { ...item, ...find };
              }
            }

            return item;
          })
          .filter((item) => item.link);
      }

      const result = await createGrowthGoalsByStudentAndDR(student._id, formData.questionId, form);
      if (result.error) {
        setError(result);
      } else {
        // onClose();
      }
    } catch (error) {
      const message = handlerErrorV2(error);
      setError({ error: true, message });
    } finally {
      setLoading(false);
    }
  };

  const onSaveMedia = async (type, data) => {
    const currentMedia = form.media || [];
    if (type === 'add') {
      setForm({
        ...form,
        media: [
          ...currentMedia,
          {
            id: data._id || new Date().getTime(),
            ...data
          }
        ]
      });
    } else {
      const media = currentMedia
        .map((item) => ({ id: item._id || item.id, ...item }))
        .filter((item) => item.id !== data.id);
      setForm({ ...form, media });
      if (data.type === 'file') {
        const result = await deleteFiles([data], 'uploads');
        if (result.error) {
          setError(result);
        }
      }
    }
  };

  const onChange = (key, value) => {
    setForm({ ...form, [key]: value });
    setError({ ...errors, [key]: null });
  };

  useEffect(() => {
    if (open) {
      onLoad();
    }
  }, [open, onLoad]);

  const Actions = (
    <div className='w-full flex gap-4 justify-between'>
      <div className='flex gap-4 justify-end items-center'>
        <ButtonStartIcon
          onClick={() => setModal({ form: {}, type: 'add' })}
          color='primary'
          variant='text'
          disabled={loading || isLoading}
          startIcon={'link'}
        >
          Attach media
        </ButtonStartIcon>
        {mode && (
          <>
            <Divider orientation='vertical' flexItem />
            <Chip
              label={mode}
              color={mode === 'editing' ? 'primary' : 'inherit'}
              className='capitalize font-bold'
            />
          </>
        )}
      </div>
      <div className='flex gap-4 justify-end'>
        <ButtonStartIcon
          onClick={onClose}
          color='primary'
          variant='outlined'
          disabled={loading}
          startIcon={['fal', 'times']}
        >
          Close
        </ButtonStartIcon>
        {permissions['by-student'] && (
          <ButtonStartIcon
            onClick={onConfirm}
            color='primary'
            variant='contained'
            disabled={loading || isLoading}
            loading={loading}
            startIcon={['fal', 'check']}
          >
            Save
          </ButtonStartIcon>
        )}
      </div>
    </div>
  );

  return (
    <>
      <Dialog
        id='question-growth-goals'
        show={open}
        title={`Growth Goals`}
        onCloseHeader={!permissions['by-student']}
        maxWidth={'md'}
        actions={permissions['by-student'] ? Actions : null}
        onClose={onClose}
      >
        <div className='flex flex-col gap-2'>
          <div className='flex gap-2 px-2 py-2 items-center bg-loading'>
            <span className=' '>
              <FontAwesomeIcon
                icon='question'
                className='text-sm p-1 text-muted size-6 opacity-50 bg-muted-foreground flex items-center justify-center rounded-full'
              />
            </span>
            <Typography variant='inherit' className='text-muted-foreground opacity-90 text-lg'>
              {formData.question}
            </Typography>
          </div>
          {isLoading ? (
            <>
              <div className='h-screen-1/2'>
                <Loading />
              </div>
            </>
          ) : (
            <>
              <Editor
                value={form.answer}
                onChange={(value) => onChange('answer', value)}
                readOnly={!permissions['by-student']}
                placeholder='The student has not entered any information yet'
                onKeyPress={() => onMode()}
                onFocus={() => onMode()}
                onBlur={() => onMode()}
              />
              {form.media && form.media.length > 0 && (
                <div className='flex flex-wrap gap-4 items-center'>
                  {form.media.map((data, index) => {
                    return (
                      <ChipMedia
                        key={`media-${index}-${data._id}-${data.type}`}
                        index={index}
                        data={data}
                        disabled={loading}
                        onDelete={() =>
                          setModal({
                            type: 'delete',
                            form: {
                              id: data._id || data.id,
                              ...data
                            }
                          })
                        }
                      />
                    );
                  })}
                </div>
              )}
            </>
          )}
          {errors && errors.message && (
            <Alert severity='error' onClose={() => setError({ message: null })}>
              {errors.message}
            </Alert>
          )}
        </div>
      </Dialog>
      {modal && (
        <DialogAttachMedia
          open={!!modal}
          onClose={() => setModal(false)}
          formData={modal.form}
          onSave={(data) => onSaveMedia(modal.type, data)}
          type={modal.type}
        />
      )}
    </>
  );
};

export default DialogGrowthGoals;
